import { useMutation } from "@tanstack/react-query";

import {
  ApiError,
  submitUpload,
  type SubmitInput,
  type SubmitResponse,
} from "@wellcheck/api-client";

import type { ParseError } from "@/features/upload/data/respondentsCsv";

/**
 * useSubmitUpload — 名簿アップロード確定 (POST /api/upload-session/submit, ADR-0025)。
 *
 * POST は orval.config.ts の方針 (query.useQuery のみ生成、mutation は低レベル関数を
 * 呼び出し側で useMutation にラップ) に従い、生成済みの submitUpload を直接呼ぶ
 * (features/respond/api/useSubmitAnswers.ts と同じパターン)。
 *
 * 400 (CSV バリデーション失敗) はサーバが frontend の ParseError と同じ形
 * ({errors: [{kind, rowNumber, message}]}) を返す (backend/handlers/officeinput_submit.go の
 * officeInputValidationErrorResponse 参照)。呼び出し側 (Wizard) がこの配列を Step1 のエラー表示に
 * そのまま流せるよう、UploadValidationError として型付きで re-throw する。
 */
export class UploadValidationError extends Error {
  override readonly name = "UploadValidationError";
  constructor(readonly errors: ParseError[]) {
    super("csv validation failed");
  }
}

export class UploadConflictError extends Error {
  override readonly name = "UploadConflictError";
  constructor() {
    super("upload submission conflicts with current campaign state");
  }
}

function isParseErrorArray(value: unknown): value is ParseError[] {
  return (
    Array.isArray(value) &&
    value.every(
      (v): v is ParseError =>
        typeof v === "object" &&
        v !== null &&
        "kind" in v &&
        (v.kind === "row" || v.kind === "file") &&
        "message" in v &&
        typeof (v as { message: unknown }).message === "string",
    )
  );
}

export function useSubmitUpload() {
  return useMutation<
    SubmitResponse,
    ApiError | UploadValidationError | UploadConflictError,
    SubmitInput
  >({
    mutationFn: async (input: SubmitInput) => {
      try {
        const response = await submitUpload(input);
        if (response.status !== 200) {
          throw new ApiError(response.status, response.data, response.headers);
        }
        return response.data;
      } catch (err) {
        if (err instanceof ApiError && err.status === 400) {
          const body = err.data as { errors?: unknown } | undefined;
          if (body && isParseErrorArray(body.errors)) {
            throw new UploadValidationError(body.errors);
          }
        }
        if (err instanceof ApiError && err.status === 409) {
          throw new UploadConflictError();
        }
        throw err;
      }
    },
  });
}
